import React, { useEffect, useState } from 'react';
import { Button, Col, Divider, Form, Input, message, Modal, Row, Select, Table, Spin } from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';
import { getUserDetails, makePostCall } from '../../../../utils/helper';
import type { OrderDetails } from '../../types';

interface CriticalFindingDialogProps {
  visible: boolean;
  orderDetails: OrderDetails | null;
  onClose: () => void;
}

const CriticalFindingDialog: React.FC<CriticalFindingDialogProps> = ({ visible, orderDetails, onClose }) => {
  const [selectedPhysicians, setSelectedPhysicians] = useState<any[]>([]);
  const [diseasesList, setDiseasesList] = useState<any[]>([]);
  const [doctorsList, setDoctorsList] = useState<any[]>([]);
  const [currentDoc, setCurrentDoc] = useState<any>(null);
  const [showOtherDisease, setShowOtherDisease] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = useForm();

  useEffect(() => {
    if (!visible) return;

    // Initialize form with referring physician name
    const refDocName = orderDetails?.pacs_order?.po_ref_doc;
    if (refDocName) {
      form.setFieldValue('doctor_name', refDocName);
    }

    // Fetch doctors and diseases lists
    makePostCall('/doctors-list', {}).then(res => setDoctorsList(res?.data?.data || [])).catch(() => setDoctorsList([]));
    makePostCall('/diseases-list', {}).then(res => setDiseasesList(res?.data?.data || [])).catch(() => setDiseasesList([]));
  }, [visible, form, orderDetails?.pacs_order?.po_ref_doc]);

  const handleSubmit = async (values: any) => {
    const { doctor_name, phone_number, findings, disease, other } = values;

    // Collect all recipients to notify
    const recipients: Array<{ doctor_name: string; phone_number?: string; ref_cd?: string; doc_email?: string; doc_mobile?: string }> = [
      { doctor_name, phone_number },
      ...selectedPhysicians.map(d => ({
        doctor_name: d.DOC_DISPLAY_NAME,
        phone_number: d.DOC_MOBILE_TEL,
        ref_cd: d.DOC_REF_CD,
        doc_email: d.DOC_EMAIL,
        doc_mobile: d.DOC_MOBILE_TEL,
      })),
    ];

    const resolvedDisease = disease === 'other' ? other : disease;

    setLoading(true);
    try {
      const promises = recipients.map(recipient =>
        makePostCall('/notify-physicians', {
          doctor_name: recipient.doctor_name,
          phone_number: recipient.phone_number || '',
          findings,
          severity: 'CRITICAL',
          patient_name: orderDetails?.pacs_order?.patient?.pat_name ?? 'Unknown',
          study_description: orderDetails?.pacs_order?.po_modality ?? '',
          order_id: String(orderDetails?.pacs_order?.pacs_ord_id ?? ''),
          user_id: getUserDetails()?.username ?? '',
          ...(resolvedDisease && { disease: resolvedDisease }),
        }),
      );

      const results = await Promise.allSettled(promises);
      const successful = results.filter(r => r.status === 'fulfilled' && r.value?.data?.success).length;
      const total = results.length;

      if (successful > 0) {
        message.success(`Critical alert sent to ${successful}/${total} physician(s)`);
        form.resetFields();
        setSelectedPhysicians([]);
        setShowOtherDisease(false);
        setCurrentDoc(null);
        onClose();
      } else {
        message.error('Failed to send alerts. Please try again.');
      }
    } catch (error) {
      message.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addDoc = () => {
    if (!currentDoc) return;
    setSelectedPhysicians(prev => [...prev, currentDoc]);
    setCurrentDoc(null);
    form.setFieldValue('ref_doc', null);
  };

  const removeDoc = (index: number) => {
    setSelectedPhysicians(prev => prev.filter((_, i) => i !== index));
  };

  const columns = [
    { title: 'Name', dataIndex: 'DOC_DISPLAY_NAME', key: 'doc_name', width: '40%' },
    { title: 'Phone', dataIndex: 'DOC_MOBILE_TEL', key: 'doc_phone', width: '35%' },
    {
      title: 'Action',
      key: 'action',
      width: '25%',
      render: (_: any, __: any, index: number) => (
        <Button type="link" danger size="small" onClick={() => removeDoc(index)}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Modal
      width={520}
      title="Critical Alert Notification"
      open={visible}
      onCancel={onClose}
      wrapClassName="critical-alert-modal"
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
          Send Alert
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div className="critical-finding-container">
          {/* Patient Header */}
          <div className="pat-header">
            <Row gutter={12}>
              <Col span={8}>
                <span className="notes-label">Name:</span>
                <span className="notes-value">{orderDetails?.pacs_order?.patient?.pat_name}</span>
              </Col>
              <Col span={8}>
                <span className="notes-label">PIN:</span>
                <span className="notes-value">{orderDetails?.pacs_order?.patient?.pat_pin}</span>
              </Col>
              <Col span={8}>
                <span className="notes-label">Accession:</span>
                <span className="notes-value">{orderDetails?.pacs_order?.po_acc_no}</span>
              </Col>
            </Row>
          </div>

          <Form form={form} onFinish={handleSubmit} layout="vertical">
            {/* Mandatory Section */}
            <div className="cf-section-label">Required Information</div>

            <Form.Item
              rules={[{ required: true, message: 'Doctor name is required' }]}
              name="doctor_name"
              label="Doctor Name"
            >
              <Input placeholder="Referring physician name" />
            </Form.Item>

            <Form.Item
              rules={[
                { required: true, message: 'Phone number is required' },
                {
                  pattern: /^[6-9]\d{9}$/,
                  message: 'Please enter a valid 10-digit mobile number starting with 6-9!',
                },
              ]}
              name="phone_number"
              label="Phone Number"
            >
              <Input maxLength={10} placeholder="10-digit phone number" />
            </Form.Item>

            <Form.Item
              rules={[{ required: true, message: 'Findings are required' }]}
              name="findings"
              label="Critical Findings"
            >
              <TextArea rows={2} placeholder="Describe the critical finding..." />
            </Form.Item>

            <Divider />

            {/* Optional Section */}
            <div className="cf-section-label">Additional Information (Optional)</div>

            <Form.Item name="disease" label="Disease">
              <Select
                allowClear
                showSearch
                placeholder="Select a disease (optional)"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  { value: 'other', label: 'Other' },
                  ...diseasesList.map(d => ({ label: d.disease_name, value: d.disease_code })),
                ]}
                onChange={val => setShowOtherDisease(val === 'other')}
              />
            </Form.Item>

            {showOtherDisease && (
              <Form.Item
                rules={[{ required: true, message: 'Please specify the disease' }]}
                name="other"
                label="Other Disease"
              >
                <Input placeholder="Specify disease type" />
              </Form.Item>
            )}

            <Form.Item label="Additional Physicians">
              <Row gutter={8}>
                <Col flex="auto">
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    allowClear
                    placeholder="Select physician (optional)"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={doctorsList.map(doc => ({
                      label: doc.DOC_DISPLAY_NAME,
                      value: doc.DOC_REF_CD,
                      docObj: doc,
                    }))}
                    onChange={(_val, rec: any) => setCurrentDoc(rec?.docObj)}
                    value={currentDoc?.DOC_REF_CD || undefined}
                  />
                </Col>
                <Col>
                  <Button
                    type="primary"
                    disabled={!currentDoc}
                    onClick={addDoc}
                  >
                    ADD
                  </Button>
                </Col>
              </Row>
            </Form.Item>

            {selectedPhysicians.length > 0 && (
              <Form.Item label="Selected Physicians">
                <Table
                  columns={columns}
                  dataSource={selectedPhysicians}
                  size="small"
                  pagination={false}
                  rowKey={(_, index) => index}
                />
              </Form.Item>
            )}
          </Form>
        </div>
      </Spin>
    </Modal>
  );
};

export default CriticalFindingDialog;
